import clsx from "clsx";
import { IconChevronDown } from "@tabler/icons-react";
import { useRouter } from "next/router";
import { useMemo } from "react";
import Link from "next/link";
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  MenuSection,
  MenuSeparator,
} from "@headlessui/react";
import { parse, format } from "date-fns";

import {
  BRAND_LOGGED_IN_START_PAGE,
  BRAND_PATHS,
  ORDER_STATUS_IDS,
  ORDER_TYPE_IDS,
} from "@chef/constants";
import { useCalendarData } from "@chef/state-management/hooks";
import {
  formatDate,
  getNextCutoff,
  isMealboxBasketType,
  orderHasBeenDelivered,
} from "@chef/helpers";
import { Card, Skeleton } from "@chef/components";
import { useOrdersQuery } from "@chef/state-management";
import { shouldShowDelivery } from "@chef/helpers";
import { isEqualStrings } from "@chef/utils/equal";
import { Breakfast, Plate, Cart } from "@chef/icons/large";
import { DeviationIndicator } from "@chef/smart-components";
import { useViewport } from "@chef/hooks";

import { Logo } from "../Logo";
import { DrawerButton } from "./Drawer/DrawerButton";
import { useSticky } from "../Sticky";

import { intl } from "./MyMenuVariant.Intl";

interface LinkWithIconProps {
  isActive: boolean;
  href: string;
  label: string;
  children: React.ReactNode;
  id: string;
  className?: string;
}

interface WeeksProps {
  isLoading: boolean;
  message: string;
  status: string;
  className?: string;
}

interface WeekInformationProps {
  href: string;
  week: number;
  isActive: boolean;
  message: string;
  id: string;
}

const parseDeliveryTime = (time?: string) => {
  const parsedTime = parse(time ?? "", "HH:mm:ss", new Date());
  return format(parsedTime, "HH:mm");
};

const WeekInformation = ({
  href,
  week,
  isActive,
  message,
  id,
}: WeekInformationProps) => {
  return (
    <MenuSection>
      <MenuItem>
        {({ focus }) => {
          const isHighlighted = isActive || focus;
          return (
            <Link
              id={id}
              href={href}
              key={week}
              className={clsx(
                "flex justify-between rounded-lg p-2 align-middle",
                isHighlighted && "bg-primary text-white font-bold",
              )}
              passHref
            >
              <span className="text-sm/5">
                {intl.WEEK} {week}
              </span>
              <span
                className={clsx("text-sm/5", !isHighlighted && "text-grey-1")}
              >
                {message}
              </span>
            </Link>
          );
        }}
      </MenuItem>
    </MenuSection>
  );
};

const LinkWithIcon = ({
  isActive,
  href,
  label,
  children,
  id,
}: LinkWithIconProps) => (
  <Link
    className="relative flex items-center gap-2 lg:gap-4 group"
    href={href}
    id={id}
  >
    {children}
    <span
      className={clsx(
        "group-hover:underline decoration-2 underline-offset-8 decoration-primary",
        isActive && "underline",
      )}
    >
      {label}
    </span>
  </Link>
);

const Weeks = ({ isLoading, message, status }: WeeksProps) => {
  const router = useRouter();

  const { week, year } = getNextCutoff();

  const { calendar } = useCalendarData({
    week,
    year,
  });

  const { order } = useOrdersQuery(
    {
      count: 1,
      orderTypes: [ORDER_TYPE_IDS.RECURRING],
      orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
    },
    {
      selectFromResult: ({ data, isLoading }) => ({
        order:
          data?.orders[0] && shouldShowDelivery(data.orders[0].deliveryDate)
            ? {
                week: data.orders[0].week,
                hasBeenDelivered: orderHasBeenDelivered(data.orders[0]),
              }
            : undefined,
        isLoading,
      }),
    },
  );

  if (isLoading) {
    return (
      <Skeleton animation="pulse" height={12} width={48} variant="rounded" />
    );
  }

  return (
    <Menu>
      {({ open }) => (
        <>
          <MenuButton
            id="delivery-date-button"
            className="flex flex-col items-center justify-center w-48 h-12 p-2 rounded hover:bg-grey-2/75"
          >
            <div className="flex items-center justify-center gap-1">
              <strong className="text-sm">{message}</strong>
              <IconChevronDown
                size={16}
                className={clsx(
                  "transition-transform duration-350",
                  open && "rotate-180",
                )}
              />
            </div>
            <p className="text-sm text-grey-1 text-nowrap">{status}</p>
          </MenuButton>
          <MenuItems anchor="bottom" className="z-50 shadow rounded-3xl">
            <Card
              noPadding
              noShadow
              noRounding
              className="flex flex-col gap-1 p-2 w-80"
            >
              {order && (
                <>
                  <WeekInformation
                    href={BRAND_PATHS.MY_DELIVERY}
                    isActive={isEqualStrings(
                      router.pathname,
                      BRAND_PATHS.MY_DELIVERY,
                    )}
                    week={order.week}
                    message={
                      order.hasBeenDelivered ? intl.DELIVERED : intl.IN_TRANSIT
                    }
                    id={`my-menu-select-week-modal-week-${order.week}`}
                  />
                  <MenuSeparator className="h-0.5 my-1 bg-grey-2" />
                </>
              )}
              {calendar
                ?.filter(Boolean)
                .slice(0, 3)
                .map((calendarWeek) => {
                  const href = BRAND_PATHS.MY_MENU_WEEK_HREF({
                    week: calendarWeek.week,
                    year: calendarWeek.year,
                  });

                  const isActive =
                    Number(router.query["week"]) === calendarWeek.week &&
                    Number(router.query["year"]) === calendarWeek.year;

                  const shippingDetails = calendarWeek.baskets.find(
                    (b) =>
                      b.week === calendarWeek.week &&
                      b.year === calendarWeek.year &&
                      isMealboxBasketType(b),
                  )?.shippingDetails;

                  const deliveryInformation =
                    shippingDetails?.plannedDeliveryInformation;

                  const isPaused = !shippingDetails?.hasDelivery;
                  const isUndeliverable =
                    !deliveryInformation?.isAvailableForDelivery;

                  let deliveryMessage = "";

                  if (isUndeliverable) {
                    deliveryMessage = intl.CLOSED;
                  } else if (isPaused) {
                    deliveryMessage = intl.CANCELLED;
                  } else if (
                    deliveryInformation?.deliveryDate &&
                    deliveryInformation?.from &&
                    deliveryInformation?.to
                  ) {
                    const deliveryDate = new Date(
                      deliveryInformation.deliveryDate,
                    );
                    const deliveryTimeFrom = parseDeliveryTime(
                      deliveryInformation.from,
                    );

                    const deliveryTimeTo = parseDeliveryTime(
                      deliveryInformation.to,
                    );

                    const deliveryDateMessage = formatDate(
                      deliveryDate,
                      intl.SHORT_DATE_FORMAT,
                    );

                    deliveryMessage = `${deliveryDateMessage} ${deliveryTimeFrom} - ${deliveryTimeTo}`;
                  }

                  return (
                    <WeekInformation
                      href={href}
                      key={calendarWeek.week}
                      isActive={isActive}
                      week={calendarWeek.week}
                      message={deliveryMessage}
                      id={`my-menu-select-week-modal-week-${calendarWeek.week}`}
                    />
                  );
                })}
            </Card>
          </MenuItems>
        </>
      )}
    </Menu>
  );
};

export const MyMenuVariant = () => {
  const router = useRouter();

  const { isFloating } = useSticky();

  const [breakpoint] = useViewport();
  const isDesktop = breakpoint !== "sm";

  const nextCutoff = getNextCutoff();

  const week = +(router.query["week"] ?? nextCutoff.week);
  const year = +(router.query["year"] ?? nextCutoff.year);
  const isOnDeliveryPage = isEqualStrings(
    router.asPath,
    BRAND_PATHS.MY_DELIVERY,
  );

  const { calendar, isLoading: isLoadingCalendar } = useCalendarData({
    week: nextCutoff.week,
    year: nextCutoff.year,
  });

  const orders = useOrdersQuery({
    count: 1,
    orderTypes: [ORDER_TYPE_IDS.RECURRING],
    orderStatuses: [ORDER_STATUS_IDS.PROCESSING, ORDER_STATUS_IDS.FINISHED],
  });

  const order = useMemo(() => {
    return orders.data?.orders[0] &&
      shouldShowDelivery(orders.data?.orders[0].deliveryDate)
      ? {
          deliveryDate: orders.data?.orders[0].deliveryDate,
          hasBeenDelivered: orderHasBeenDelivered(orders.data?.orders[0]),
        }
      : undefined;
  }, [orders]);

  const deliveryDateMessage = useMemo(() => {
    if (isOnDeliveryPage) {
      return formatDate(
        order?.deliveryDate ?? new Date().getTime(),
        intl.SHORT_DATE_FORMAT,
      );
    }

    const deliveryDate = calendar
      ?.find((cw) => cw.week === week && cw.year === year)
      ?.baskets?.find((basket) => isMealboxBasketType(basket))?.shippingDetails
      .plannedDeliveryInformation.deliveryDate;

    return formatDate(
      new Date(deliveryDate ?? new Date().getTime()),
      intl.SHORT_DATE_FORMAT,
    );
  }, [order, calendar, week, year, isOnDeliveryPage]);

  const deliveryStatusMessage = useMemo(() => {
    if (isOnDeliveryPage) {
      return order?.hasBeenDelivered ? intl.DELIVERED : intl.IN_TRANSIT;
    }

    const cutoffDateTime = calendar
      ?.find((cw) => cw.week === week && cw.year === year)
      ?.baskets?.find((basket) => isMealboxBasketType(basket))?.shippingDetails
      .plannedDeliveryInformation.cutOffDateTime;

    const date = new Date(cutoffDateTime ?? new Date().getTime());

    return `${intl.MAKE_CHANGES_BY} ${formatDate(date, intl.LONG_DATE_FORMAT)}`;
  }, [order, isOnDeliveryPage, calendar, week, year]);

  const isFloatingAndDesktop = isFloating && isDesktop;
  const isNotFloatingAndDesktop = !isFloating && isDesktop;

  return (
    <div className="flex-col gap-2">
      <div
        id="my-menu-header"
        className="relative flex items-center justify-between w-full h-15"
      >
        <div className="flex w-24 md:w-48">
          {isFloatingAndDesktop ? (
            <nav className="flex items-center gap-7 shrink-0">
              <LinkWithIcon
                href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
                label={intl.MEALS}
                id="header-nav-dishes"
                isActive={[
                  BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
                  BRAND_PATHS.MY_MENU_PAGE,
                ].some((link) => isEqualStrings(router.asPath, link))}
              >
                <Plate className="w-6 h-6" />
              </LinkWithIcon>

              <LinkWithIcon
                href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
                label={intl.ADDONS}
                id="header-nav-addons"
                isActive={isEqualStrings(
                  router.asPath,
                  BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
                )}
              >
                <Breakfast className="w-6 h-6" />
              </LinkWithIcon>
            </nav>
          ) : (
            <Logo responsive href={BRAND_LOGGED_IN_START_PAGE} />
          )}
        </div>

        <Weeks
          isLoading={isLoadingCalendar}
          message={deliveryDateMessage}
          status={deliveryStatusMessage}
        />

        <div className="flex items-center justify-end w-24 gap-4 md:w-48">
          {isFloatingAndDesktop && (
            <LinkWithIcon
              className="relative"
              href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({
                week,
                year,
              })}
              label={intl.ORDER}
              id="header_nav_checkout"
              isActive={[
                BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year }),
                BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }),
              ].some((link) => isEqualStrings(router.asPath, link))}
            >
              <DeviationIndicator
                week={week}
                year={year}
                className="absolute left-4 -top-2"
              />
              <Cart className="w-6 h-6" />
            </LinkWithIcon>
          )}

          <DrawerButton />
        </div>
      </div>

      {isNotFloatingAndDesktop && (
        <div className="flex items-center justify-between pb-4 h-15">
          <div className="flex gap-7">
            <LinkWithIcon
              href={BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year })}
              label={intl.MEALS}
              id="header-nav-dishes"
              isActive={[
                BRAND_PATHS.MY_MENU_WEEK_HREF({ week, year }),
                BRAND_PATHS.MY_MENU_PAGE,
              ].some((link) => isEqualStrings(router.asPath, link))}
            >
              <Plate className="w-6 h-6" />
            </LinkWithIcon>

            <LinkWithIcon
              href={BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year })}
              label={intl.ADDONS}
              id="header-nav-addons"
              isActive={isEqualStrings(
                router.asPath,
                BRAND_PATHS.WEEK_EDITOR_STANDALONE_PAGE({ week, year }),
              )}
            >
              <Breakfast className="w-6 h-6" />
            </LinkWithIcon>
          </div>
          <LinkWithIcon
            className="relative mr-2 lg:mr-4"
            href={BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year })}
            label={intl.ORDER}
            id="header_nav_checkout"
            isActive={[
              BRAND_PATHS.WEEK_EDITOR_CONFIRM_ORDER_PAGE({ week, year }),
              BRAND_PATHS.WEEK_EDITOR_CONFIRMATION_PAGE({ week, year }),
            ].some((link) => isEqualStrings(router.asPath, link))}
          >
            <DeviationIndicator
              week={week}
              year={year}
              className="absolute left-4 -top-2"
            />
            <Cart className="w-6 h-6" />
          </LinkWithIcon>
        </div>
      )}
    </div>
  );
};
